<template>
  <transition name="modal-fade">
    <div v-if="show" class="modal-overlay" @click.self="handleClose">
      <div class="modal-container" :class="modalClass">
        <div class="modal-header">
          <h3>{{ title }}</h3>
          <button v-if="closable" class="close-btn" @click="handleClose">×</button>
        </div>
        <div class="modal-body">
          <div v-if="icon" class="modal-icon" :class="iconClass">
            <span>{{ icon }}</span>
          </div>
          <p class="modal-message">{{ message }}</p>
        </div>
        <div class="modal-footer">
          <button
            v-for="(btn, idx) in buttons"
            :key="idx"
            :class="['modal-btn', btn.class || 'btn-secondary']"
            @click="handleButtonClick(btn)"
          >
            {{ btn.text }}
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'JourneyModal',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: 'Notification'
    },
    message: {
      type: String,
      required: true
    },
    icon: {
      type: String,
      default: null // '💾', '⚠️', 'ℹ️', '✓'
    },
    type: {
      type: String,
      default: 'info', // 'info', 'warning', 'success', 'error'
      validator: (value) => ['info', 'warning', 'success', 'error'].includes(value)
    },
    buttons: {
      type: Array,
      default: () => [{ text: 'OK', action: 'close', class: 'btn-primary' }]
    },
    closable: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    modalClass() {
      return `modal-${this.type}`;
    },
    iconClass() {
      return `icon-${this.type}`;
    }
  },
  methods: {
    handleClose() {
      if (this.closable) {
        this.$emit('close');
      }
    },
    handleButtonClick(button) {
      this.$emit('action', button.action);
      if (button.action === 'close') {
        this.handleClose();
      }
    }
  }
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: grid;
  place-items: center;
  z-index: 9999;
  backdrop-filter: blur(2px);
}

.modal-container {
  background: #fffef9;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  max-width: 500px;
  width: 90%;
  overflow: hidden;
  animation: modalSlideIn 0.3s ease-out;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.modal-header {
  padding: 20px 24px;
  border-bottom: 2px solid #e37267;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: linear-gradient(135deg, #ffe1cf 0%, #fffef9 100%);
}

.modal-header h3 {
  margin: 0;
  color: #e37267;
  font-size: 20px;
  font-weight: 600;
}

.close-btn {
  background: transparent;
  border: none;
  font-size: 32px;
  color: #999;
  cursor: pointer;
  line-height: 1;
  transition: color 0.2s;
}

.close-btn:hover {
  color: #e37267;
}

.modal-body {
  padding: 32px 24px;
  text-align: center;
}

.modal-icon {
  font-size: 48px;
  margin-bottom: 16px;
  animation: iconBounce 0.5s ease-out;
}

@keyframes iconBounce {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.1); }
}

.icon-info { color: #5469d4; }
.icon-warning { color: #f59e0b; }
.icon-success { color: #10b981; }
.icon-error { color: #ef4444; }

.modal-message {
  font-size: 16px;
  line-height: 1.6;
  color: #333;
  margin: 0;
}

.modal-footer {
  padding: 16px 24px 24px;
  display: flex;
  gap: 12px;
  justify-content: center;
}

.modal-btn {
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 100px;
}

.btn-primary {
  background: #e37267;
  color: white;
}

.btn-primary:hover {
  background: #d66259;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(227, 114, 103, 0.3);
}

.btn-secondary {
  background: #f2f2f2;
  color: #333;
  border: 2px solid #ddd;
}

.btn-secondary:hover {
  background: #e6e6e6;
  border-color: #ccc;
}

.btn-danger {
  background: #ef4444;
  color: white;
}

.btn-danger:hover {
  background: #dc2626;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-active .modal-container {
  animation: modalSlideIn 0.3s ease-out;
}

.modal-fade-leave-active .modal-container {
  animation: modalSlideOut 0.3s ease-in;
}

@keyframes modalSlideOut {
  from {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  to {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }
}
</style>
